import type { Database, Tables, TablesInsert, TablesUpdate } from './database'

// ===========================================
// HELPERS DE TIPOS (re-export de Views)
// ===========================================

export type Views<T extends keyof Database['public']['Views']> = Database['public']['Views'][T]['Row']

// ===========================================
// ALIASES COMUNES
// ===========================================

// BrainHub Core
export type Organizacion = Tables<'organizaciones'>
export type OrganizacionInsert = TablesInsert<'organizaciones'>
export type Persona = Tables<'personas'>
export type PersonaInsert = TablesInsert<'personas'>
export type ClienteV2 = Tables<'clientes'>
export type ClienteV2Insert = TablesInsert<'clientes'>
export type UsuarioBH = Tables<'usuarios_brainhub'>
export type UsuarioOrganizacion = Tables<'usuario_organizaciones'>
export type FacetaContable = Tables<'cliente_faceta_contable'>
export type FacetaContableInsert = TablesInsert<'cliente_faceta_contable'>
export type FacetaJuridica = Tables<'cliente_faceta_juridica'>

// Vistas BrainHub
export type VistaClienteCompleto = Views<'vista_clientes_completos'>
export type VistaClienteCompletoV2 = Views<'vista_clientes_completos_v2'>
export type VistaUsuarioOrganizacion = Views<'vista_usuarios_organizacion_v2'>

// Tablas legacy (v1)
export type Cliente = Tables<'clientes'>
export type ClienteInsert = TablesInsert<'clientes'>

// Movimientos con tipo_deuda específico
export type TipoDeuda = 'mensual' | 'esporadica'
export type Movimiento = Omit<Tables<'movimientos'>, 'tipo_deuda'> & {
  tipo_deuda: TipoDeuda | null
}
export type MovimientoInsert = Omit<TablesInsert<'movimientos'>, 'tipo_deuda'> & {
  tipo_deuda?: TipoDeuda | null
}

export type Cuenta = Tables<'cuentas'>
export type Servicio = Tables<'servicios'>
export type Presupuesto = Tables<'presupuestos'>
export type Tarea = Tables<'tareas'>
export type Documento = Tables<'documentos'>
export type VencimientoFiscal = Tables<'vencimientos_fiscales'>
export type DeclaracionJurada = Tables<'declaraciones_juradas'>
export type EmpleadoCliente = Tables<'empleados_clientes'>

// Contratos
export type ContratoCliente = Tables<'contratos_cliente'>
export type ContratoClienteInsert = TablesInsert<'contratos_cliente'>
export type ContratoClienteUpdate = TablesUpdate<'contratos_cliente'>

// Otros
export type VistaSaldoCliente = Views<'vista_saldos_clientes'>
export type ConvenioColectivo = Tables<'convenios_colectivos'>

// ===========================================
// TIPOS PERSONALIZADOS
// ===========================================

export interface OtraClave {
  nombre: string
  clave: string
}

export interface ActividadEconomica {
  codigo: string
  descripcion: string
}

// Estados
export type EstadoCliente = 'activo' | 'inactivo' | 'suspendido'
export type EstadoVencimiento = 'pendiente' | 'presentado' | 'vencido'
export type EstadoTarea = 'pendiente' | 'en_proceso' | 'completada'
export type EstadoEmpleado = 'activo' | 'baja' | 'licencia'
export type EstadoDDJJ = 'presentada' | 'con_observacion' | 'con_intimacion' | 'rectificada'

export type TipoImpuesto =
  | 'IVA'
  | 'Ganancias'
  | 'Bienes Personales'
  | 'IIBB Tucumán'
  | 'Monotributo'
  | 'SUSS'
  | 'Autónomos'
  | 'Otro'

export type CondicionIVA =
  | 'responsable_inscripto'
  | 'monotributista'
  | 'exento'
  | 'consumidor_final'
  | 'no_responsable'

export type RolOrganizacion = 'owner' | 'admin' | 'contador' | 'asistente' | 'solo_lectura'
export type WorkspaceId = 'contable' | 'juridico'
export type TipoPersona = 'fisica' | 'juridica' | 'sucesion_indivisa'

// ===========================================
// TIPOS EXTENDIDOS
// ===========================================

export interface VencimientoConCliente extends VencimientoFiscal {
  cliente_nombre: string | null
  cliente_cuit: number | null
  cliente_responsable: string | null
  cliente_email: string | null
  dias_para_vencer: number | null
  tipo_impuesto?: string | null
}

export interface VencimientosDelDia {
  fecha: string
  vencimientos: VencimientoConCliente[]
  totalPendientes: number
  totalPresentados: number
  totalVencidos: number
}

export interface EstadisticasVencimientos {
  pendientes: number
  presentados: number
  vencidos: number
  proximaSemana: number
  proximoMes: number
}

export interface ResultadoGeneracionVencimientos {
  clientes_procesados: number
  vencimientos_creados: number
  vencimientos_omitidos: number
}

export interface MarcarPresentadoData {
  fecha_presentacion: string
  numero_presentacion?: string
  observaciones?: string
}

export interface ClienteConFacetaContable {
  id: string
  persona_id: string | null
  organizacion_id: string | null
  workspaces: string[] | null
  created_at: string | null
  updated_at: string | null
  estado: string | null
  fecha_alta: string | null
  fecha_baja: string | null
  motivo_baja: string | null
  motivo_baja_detalle: string | null
  descripcion: string | null
  codigo_cliente: string | null
  afip_acceso_directo_optin: boolean | null
  responsables?: { usuario_id: string }[] | null
  // Convenience fields (flattened from persona/faceta_contable for legacy compatibility)
  nombre?: string | null
  cuit?: string | null
  condicion_iva?: string | null
  persona: {
    id?: string
    tipo?: 'fisica' | 'juridica' | 'sucesion_indivisa'
    nombre?: string | null
    apellido?: string | null
    razon_social?: string | null
    nombre_completo: string | null
    nombre_fantasia?: string | null
    cuit: string | null
    email: string | null
    telefono: string | null
    domicilio_calle?: string | null
    domicilio_numero?: string | null
    domicilio_piso?: string | null
    domicilio_depto?: string | null
    domicilio_cp?: string | null
    domicilio_localidad?: string | null
    domicilio_provincia?: string | null
    domicilio_barrio?: string | null
    domicilio_referencia?: string | null
    created_by?: string | null
    created_at?: string | null
    updated_at?: string | null
    admin_cuit?: string | null
    admin_nombre?: string | null
    padron_sincronizado_en?: string | null
  } | null
  delegacion_eventanilla?: string | null
  faceta_contable: {
    id?: string
    condicion_iva: string | null
    categoria_monotributo: string | null
    actividad_principal: string | null
    codigo_actividad_afip: string | null
    actividades_economicas: ActividadEconomica[] | null
    cierre_ejercicio: string | null
    impuestos_inscriptos: string[] | null
    clave_fiscal: string | null
    otras_claves: OtraClave[] | null
    honorarios_mensuales: number | null
    honorarios_desde: string | null
    provincia_principal: string | null
    regimen_iibb?: 'local' | 'convenio_multilateral' | 'simplificado' | 'no_gravado' | null
    impuestos_liquidar?: unknown[] | null
    inscripcion_numero?: string | null
    inscripcion_fojas?: string | null
    inscripcion_tomo?: string | null
    inscripcion_protocolo?: string | null
    inscripcion_fecha?: string | null
    inscripcion_ano?: number | null
  } | null
}

export interface MovimientoConCliente extends Movimiento {
  cliente: {
    id: string
    persona?: {
      nombre_completo?: string | null
      cuit?: string | null
    }
  } | null
  _adjuntoDocumentoId?: string | null
  _aplicacionEstado?: 'pendiente' | 'parcial' | 'pagado' | 'sin_aplicar' | 'aplicado_completo' | null
  _aplicacionMontoAplicado?: number | null
  _aplicacionSaldoPendiente?: number | null
}

export type CategoriaDocumento =
  | 'poderes'
  | 'balances'
  | 'ddjj'
  | 'contratos'
  | 'constancias'
  | 'identificacion'
  | 'otros'

export interface DocumentoConCliente extends Documento {
  cliente_nombre: string | null
  cliente_cuit: number | null
  dias_restantes: number | null
}

export interface DeclaracionJuradaCompletaDB extends DeclaracionJurada {
  cliente_nombre: string | null
  cliente_cuit: number | null
  cliente_tipo_contribuyente: string | null
  documento_nombre: string | null
  documento_path: string | null
  ddjj_original_periodo: string | null
  ddjj_original_tipo: string | null
  vencimiento_fecha: string | null
}

export type TipoContrato = 'tiempo_completo' | 'tiempo_parcial' | 'eventual' | 'pasantia'
export type ModalidadEmpleado = 'presencial' | 'remoto' | 'hibrido'
export type MotivoAumentoSueldo = 'aumento' | 'promocion' | 'ajuste_inflacion' | 'convenio' | 'actualizacion' | 'otro'
export type TipoNovedadAfip = 'alta_pendiente' | 'baja_pendiente'

export interface EmpleadoCompletoDB extends EmpleadoCliente {
  cliente_nombre: string | null
  cliente_cuit: number | null
  convenio_codigo: string | null
  convenio_nombre: string | null
  alta_afip_pendiente: boolean | null
  baja_afip_pendiente: boolean | null
}

export interface HistorialSueldoDB {
  id: string
  empleado_id: string
  sueldo_anterior: number | null
  sueldo_nuevo: number
  fecha_vigencia: string
  motivo: MotivoAumentoSueldo | null
  observaciones: string | null
  created_by: string
  created_at: string | null
}

export interface NovedadAfipPendienteDB {
  id: string
  nombre: string
  cuil: string
  cliente_id: string
  cliente_nombre: string
  tipo_novedad: TipoNovedadAfip
  fecha_ingreso: string
  fecha_egreso: string | null
  dias_desde_ingreso: number | null
  dias_desde_egreso: number | null
}

export interface ResumenEmpleadosClienteDB {
  cliente_id: string
  cliente_nombre: string
  empleados_activos: number
  empleados_baja: number
  empleados_licencia: number
  total_historico: number
  altas_afip_pendientes: number
  bajas_afip_pendientes: number
}

export type Prioridad = 'urgente' | 'alta' | 'media' | 'baja'

export interface TareaCompleta extends Tarea {
  cliente_nombre: string | null
  cliente_cuit: number | null
  tipo_impuesto: string | null
  vencimiento_periodo: string | null
  fecha_vencimiento: string | null
  dias_restantes: number | null
  cantidad_comentarios: number
  grupo_clave: string | null
  workspace: string | null
  vinculado_tipo: string | null
  vinculado_id: string | null
}

export interface TareaComentario {
  id: string
  tarea_id: string
  contenido: string
  autor: string
  created_at: string | null
}

// ===========================================
// TIPOS PARA SERVICIOS
// ===========================================

export type TipoFacturacion =
  | 'por_hora'
  | 'mensual'
  | 'por_presentacion'
  | 'precio_fijo'
  | 'a_convenir'

export type CategoriaServicio =
  | 'impuestos_nacionales'
  | 'impuestos_provinciales'
  | 'sueldos'
  | 'estados_contables'
  | 'inscripciones'
  | 'asesoramiento'
  | 'societario'
  | 'otros'

export type EstadoServicio =
  | 'activo'
  | 'inactivo'
  | 'solo_existentes'

export type PeriodicidadServicio =
  | 'unico'
  | 'mensual'
  | 'bimestral'
  | 'trimestral'
  | 'cuatrimestral'
  | 'semestral'
  | 'anual'
  | 'segun_vencimiento'
  | 'no_aplica'

export interface ServicioCompleto extends Servicio {
  precio_estimado: number | null
  tipo_facturacion: TipoFacturacion
  categoria: CategoriaServicio
  estado: EstadoServicio
  periodicidad: PeriodicidadServicio | null
  total_clientes?: number
}

export const TIPO_FACTURACION_LABELS: Record<TipoFacturacion, string> = {
  por_hora: 'Por Hora',
  mensual: 'Mensual/Recurrente',
  por_presentacion: 'Por Presentación',
  precio_fijo: 'Precio Fijo',
  a_convenir: 'A Convenir',
}

export const CATEGORIA_SERVICIO_LABELS: Record<CategoriaServicio, string> = {
  impuestos_nacionales: 'Impuestos Nacionales',
  impuestos_provinciales: 'Impuestos Provinciales',
  sueldos: 'Liquidación de Sueldos',
  estados_contables: 'Estados Contables',
  inscripciones: 'Inscripciones y Trámites',
  asesoramiento: 'Asesoramiento',
  societario: 'Societario',
  otros: 'Otros',
}

export const ESTADO_SERVICIO_LABELS: Record<EstadoServicio, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  solo_existentes: 'Solo Clientes Existentes',
}

export const PERIODICIDAD_SERVICIO_LABELS: Record<PeriodicidadServicio, string> = {
  unico: 'Único (una sola vez)',
  mensual: 'Mensual',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  cuatrimestral: 'Cuatrimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  segun_vencimiento: 'Según Vencimiento',
  no_aplica: 'No Aplica',
}

// ===========================================
// TIPOS PARA PRESUPUESTOS
// ===========================================

export interface PresupuestoItem {
  servicio_id: string
  cantidad: number
  precio_unitario: number
  descuento_porcentaje?: number
  subtotal: number
  descripcion?: string | null
}

export type EstadoPresupuesto = 'borrador' | 'enviado' | 'aceptado' | 'rechazado'

export const ESTADO_PRESUPUESTO_LABELS: Record<EstadoPresupuesto, string> = {
  borrador: 'Borrador',
  enviado: 'Enviado',
  aceptado: 'Aceptado',
  rechazado: 'Rechazado',
}

export interface PresupuestoConItems extends Omit<Presupuesto, 'items'> {
  items: PresupuestoItem[]
  facturado?: boolean
}

